/* eslint-disable react/destructuring-assignment */
import { ActionIcon, Group, Paper, SimpleGrid, Tooltip } from '@mantine/core';
import { useModals } from '@mantine/modals';
import { IconCalculator, IconCopy, IconEdit, IconEye, IconPrinter } from '@tabler/icons-react';
import DeleteConfirmPopover from '../../../../../components/DeleteConfirmPopover';
import { ProposalStatusEnum } from '../../../../../business/proposals/status';
import PageViewProperty from '../../../../../components/core/PageViewProperty/PageViewProperty';
import useCurrentUser from '../../../../../hooks/useCurrentUser';
import { Permission } from '../../../../../models/core/departments.type';
import { ProposalSearchResponseType } from '../../../../../models/core/proposals.type';
import theme from '../../../../../theme';
import { validate } from '../../../../../utils/permission.utils';

type DataViewActionProps = {
  item: ProposalSearchResponseType;
  confirmActionResult(item: ProposalSearchResponseType, action: string, confirmed: boolean): void;
};

export default function DataViewActions(props: DataViewActionProps) {
  const [currentUser] = useCurrentUser();
  const modals = useModals();

  const showModal = (item: ProposalSearchResponseType, action: string) => {
    modals.openConfirmModal({
      title: `Gostaria de ${action} essa proposta?`,
      size: 'xl',
      closeOnClickOutside: false,
      closeOnEscape: false,
      children: (
        <Paper shadow="xs" p="md" withBorder>
          <SimpleGrid cols={3}>
            <PageViewProperty label="#" text={item.idProposta.toString()} size="sm" />
            <PageViewProperty label="CNPJ/CPF" text={item.clienteCNPJ || item.clienteCPF || '-'} size="sm" />
            <PageViewProperty
              label="Nome Fantasia/Nome"
              text={item.clienteNomeFantasia || item.clienteNome || '-'}
              size="sm"
            />
          </SimpleGrid>
        </Paper>
      ),
      onCancel: () => {
        props.confirmActionResult(item, action, false);
      },
      onConfirm: () => {
        props.confirmActionResult(item, action, true);
      },
    });
  };

  const validateMyOwnProposal = () => {
    if (
      validate(Permission.ProposalOwn, currentUser.permissoes) &&
      props.item.idCriadoPor !== currentUser.idUsuario
    ) {
      return false;
    }
    return true;
  };

  return (
    <Group>
      {validate(Permission.ProposalView, currentUser.permissoes) && validateMyOwnProposal() && (
        <Tooltip withArrow transitionProps={{ transition: 'fade', duration: 200 }} label="Visualizar">
          <ActionIcon
            size="sm"
            radius="sm"
            color="primary"
            style={{ borderColor: theme?.colors?.primary?.[6] }}
            variant="outline"
            onClick={() => {
              props.confirmActionResult(props.item, 'visualizar', true);
            }}
          >
            <IconEye size={15} color={theme?.colors?.primary?.[6]} />
          </ActionIcon>
        </Tooltip>
      )}
      {props.item.codigoPropostaStatus === ProposalStatusEnum.EmEdicao &&
        validate(Permission.ProposalEdit, currentUser.permissoes) &&
        validateMyOwnProposal() && (
          <Tooltip withArrow transitionProps={{ transition: 'fade', duration: 200 }} label="Editar">
            <ActionIcon
              size="sm"
              color="primary"
              style={{ borderColor: theme?.colors?.accent?.[6] }}
              variant="outline"
              onClick={() => {
                props.confirmActionResult(props.item, 'editar', true);
              }}
            >
              <IconEdit size={15} color={theme?.colors?.accent?.[6]} />
            </ActionIcon>
          </Tooltip>
        )}
      {validate(Permission.ProposalAdd, currentUser.permissoes) && validateMyOwnProposal() && (
        <Tooltip withArrow transitionProps={{ transition: 'fade', duration: 200 }} label="Duplicar">
          <ActionIcon
            size="sm"
            radius="sm"
            color="primary"
            style={{ borderColor: theme?.colors?.gray?.[6] }}
            variant="outline"
            onClick={() => {
              showModal(props.item, 'duplicar');
            }}
          >
            <IconCopy size={15} color={theme?.colors?.gray?.[6]} />
          </ActionIcon>
        </Tooltip>
      )}
      {props.item.codigoPropostaStatus === ProposalStatusEnum.Finalizado &&
        validate(Permission.ProposalAdd, currentUser.permissoes) &&
        validateMyOwnProposal() && (
          <Tooltip withArrow transitionProps={{ transition: 'fade', duration: 200 }} label="Reajustar">
            <ActionIcon
              size="sm"
              color="primary"
              style={{ borderColor: '##15AABF' }}
              variant="outline"
              onClick={() => {
                showModal(props.item, 'reajustar');
              }}
            >
              <IconCalculator size={15} color="#15AABF" />
            </ActionIcon>
          </Tooltip>
        )}
      {validate(Permission.ProposalPrint, currentUser.permissoes) && validateMyOwnProposal() && (
        <Tooltip withArrow transitionProps={{ transition: 'fade', duration: 200 }} label="Imprimir">
          <ActionIcon
            size="sm"
            radius="sm"
            color="primary"
            style={{ borderColor: theme?.colors?.dark?.[6] }}
            variant="outline"
            onClick={() => {
              showModal(props.item, 'imprimir');
            }}
          >
            <IconPrinter size={15} color={theme?.colors?.dark?.[6]} />
          </ActionIcon>
        </Tooltip>
      )}
      {validate(Permission.ProposalDelete, currentUser.permissoes) && validateMyOwnProposal() && (
        <DeleteConfirmPopover
          label="Excluir"
          message="Esta ação removerá a proposta."
          onConfirm={() => {
            props.confirmActionResult(props.item, 'excluir', true);
          }}
          variant="outline"
        />
      )}
    </Group>
  );
}
